<template>
  <div
    class="studio-community-card"
    v-bind="$attrs"
    :class="{ 'left-group': isUserLeftThisGroup(group) }"
  >
    <HMFixedAspectRatioContainer>
      <StudioCommunityGroupThumbnail
        :group="group"
        @click="$emit('click')"
        :class="{ animated: animated }"
      />
    </HMFixedAspectRatioContainer>
    <div class="outer-container d-flex flex-column align-items-start">
      <div class="description flex-grow-1 text-truncate">
        <div
          class="d-flex flex-column justify-content-between align-items-start"
        >
          <div class="d-flex justify-content-between w-100 align-items-center">
            <div class="group-name-container h4 m-0 mb-2">
              {{ group.group_name }}
            </div>
            <div>
              <HMIconButton
                class="dots-menu"
                :icon="['far', 'fa-ellipsis-h']"
                :id="`popover-${group.id}`"
              />
              <HMPopover :target="`popover-${group.id}`" placement="bottom-end" :clickTrigger="false">
                <HMPopoverMenuItem
                  v-if="isUserLeftThisGroup(group)"
                  @click="rejoinGroup(group)"
                >
                  {{ $t('COMMUNITY.LINK.REJOIN_GROUP') }}
                </HMPopoverMenuItem>
                <HMPopoverMenuItem v-else @click="leaveGroup(group)">
                  {{ $t('COMMUNITY.LINK.LEAVE_GROUP') }}
                </HMPopoverMenuItem>
              </HMPopover>
            </div>
          </div>
          <div class="d-flex justify-content-between">
            {{
              $t('COMMUNITY.TEXT.POSTS_VARIANT', group.num_posts, {
                count: group.num_posts,
              })
            }}
            <span v-if="group.num_posts > 0" class="ms-1">
              | {{ dateFromNow(group.last_post_datetime) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import moment from '@/services/moment';
import StudioCommunityGroupThumbnail from '@/components/community/StudioCommunityGroupThumbnail.vue';
import { errorToMessage } from '@/utils/errors';
import { useCommunityStore } from '@/stores';

export default {
  name: 'StudioCommunityGroupCard',
  components: {
    StudioCommunityGroupThumbnail,
  },
  props: {
    group: {
      type: Object as () => CommunityGroupsResponse,
      required: true,
    },
    animated: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    studioURL() {
      return this.$studioURL;
    },
  },
  methods: {
    dateFromNow(date) {
      return moment(date).fromNow();
    },
    isUserLeftThisGroup(group) {
      return group.user_left_this_group;
    },
    async rejoinGroup(group) {
      try {
        await useCommunityStore().rejoinGroup({
          groupId: group.id,
          studioURL: this.studioURL,
        });
        group.user_left_this_group = false;
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'COMMUNITY.NOTIFICATIONS.YOU_SUCCESSFULLY_REJOINED_GROUP'
          ),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
    async leaveGroup(group) {
      try {
        await useCommunityStore().leaveGroup({
          groupId: group.id,
          studioURL: this.studioURL,
        });
        group.user_left_this_group = true;
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('COMMUNITY.NOTIFICATIONS.YOU_SUCCESSFULLY_LEFT_GROUP'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/components-variables.scss';
.studio-community-card {
  &:hover {
    .group-name-container,
    .dots-menu {
      color: var(--brand-color);
    }
    .community-group-thumbnail__image {
      &.animated {
        box-shadow: 0 12px 12px -6px $dark-gray;
        transform: translateY(-16px);
      }
    }
  }

  cursor: pointer;
  position: relative;

  .description {
    background-color: $white;
    position: relative;
    bottom: 0;
    left: 0;
    padding-top: 1rem;
    border-top-right-radius: 6px;
    width: 100%;
  }

  .group-name-container {
    white-space: normal;
    line-height: 1.5;
  }
}
.left-group {
  opacity: 0.5;
  cursor: default;
}
</style>
